// src/components/clients/LiquidarCargoModal.jsx
import React, { useState, useEffect } from 'react';
import {
    Modal,
    Table,
    Button,
    Typography,
    Space,
    Alert,
    Divider,
    Tooltip,
} from 'antd';
import {
    DeleteOutlined,
    CheckCircleOutlined,
    ExclamationCircleOutlined,
    InfoCircleOutlined,
} from '@ant-design/icons';
import { runningAccountService } from '../../services/runningAccountService';

const { Text, Title } = Typography;

const formatPrice = (value) => {
    if (typeof value !== 'number' || isNaN(value)) return '$ 0,00';
    return `$ ${Math.abs(value).toLocaleString('es-AR', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    })}`;
};

const formatDate = (date) => {
    if (!date) return '-';
    const dateStr = String(date).split('T')[0];
    if (!dateStr || !dateStr.includes('-')) return '-';
    const [year, month, day] = dateStr.split('-');
    return `${day}/${month}/${year}`;
};

/**
 * LiquidarCargoModal
 *
 * Props:
 *   visible          {boolean}   - controls modal visibility
 *   initialCargos    {object[]}  - CARGO movements pre-selected by the user
 *   availableCredit  {number}    - client's current available credit (>= 0)
 *   onConfirm        {function}  - called with array of movement IDs to settle
 *   onClose          {function}  - closes the modal
 *   loading          {boolean}   - shows loading state on Confirm button
 */
const LiquidarCargoModal = ({
    visible,
    initialCargos = [],
    availableCredit = 0,
    onConfirm,
    onClose,
    loading = false,
}) => {
    // Local copy of cargos — user can remove rows before confirming
    const [localCargos, setLocalCargos] = useState([]);

    // Reset local list every time the modal opens with new cargos
    useEffect(() => {
        if (visible) {
            setLocalCargos(initialCargos);
        }
    }, [visible, initialCargos]); // eslint-disable-line

    const handleRemove = (id) => {
        setLocalCargos(prev => prev.filter(c => c.id !== id));
    };

    // Live calculation
    const { batchTotal, shortfall, canSettle, creditAfter } =
        runningAccountService.calculateSettlement(localCargos, availableCredit);

    const handleConfirm = () => {
        if (!canSettle || localCargos.length === 0) return;
        onConfirm(localCargos.map(c => c.id));
    };

    const columns = [
        {
            title: 'Fecha',
            dataIndex: 'fecha',
            key: 'fecha',
            width: 100,
            render: (v) => formatDate(v),
        },
        {
            title: 'Descripción',
            dataIndex: 'descripcion',
            key: 'descripcion',
            ellipsis: true,
        },
        {
            title: 'DEBE',
            dataIndex: 'debe',
            key: 'debe',
            width: 130,
            align: 'right',
            render: (v) => (
                <Text strong style={{ color: '#cf1322' }}>
                    {formatPrice(v)}
                </Text>
            ),
        },
        {
            title: '',
            key: 'remove',
            width: 40,
            align: 'center',
            render: (_, record) => (
                <Tooltip title="Quitar de esta liquidación">
                    <Button
                        type="text"
                        size="small"
                        icon={<DeleteOutlined />}
                        style={{ color: '#ff4d4f' }}
                        onClick={() => handleRemove(record.id)}
                        disabled={localCargos.length === 1}
                    />
                </Tooltip>
            ),
        },
    ];

    return (
        <Modal
            title={
                <Space>
                    <CheckCircleOutlined style={{ color: '#faad14' }} />
                    <span>Liquidar CARGOs seleccionados</span>
                </Space>
            }
            open={visible}
            onCancel={onClose}
            width={680}
            footer={[
                <Button key="cancel" onClick={onClose} disabled={loading}>
                    Cancelar
                </Button>,
                <Tooltip
                    key="confirm"
                    title={
                        !canSettle
                            ? `Crédito insuficiente. Faltan ${formatPrice(shortfall)} para completar esta operación.`
                            : localCargos.length === 0
                            ? 'Agregá al menos un CARGO.'
                            : ''
                    }
                >
                    <Button
                        type="primary"
                        danger={!canSettle}
                        disabled={!canSettle || localCargos.length === 0}
                        loading={loading}
                        onClick={handleConfirm}
                        icon={<CheckCircleOutlined />}
                        style={
                            canSettle && localCargos.length > 0
                                ? { backgroundColor: '#52c41a', borderColor: '#52c41a' }
                                : {}
                        }
                    >
                        Confirmar liquidación
                    </Button>
                </Tooltip>,
            ]}
        >
            {/* CARGO table */}
            <Table
                dataSource={localCargos}
                columns={columns}
                rowKey="id"
                size="small"
                pagination={false}
                locale={{ emptyText: 'Sin CARGOs seleccionados' }}
                style={{ marginBottom: 16 }}
            />

            <Divider style={{ margin: '12px 0' }} />

            {/* Live calculation panel */}
            <div
                style={{
                    background: '#fafafa',
                    border: '1px solid #f0f0f0',
                    borderRadius: 8,
                    padding: '16px 20px',
                }}
            >
                <Title level={5} style={{ marginBottom: 12, marginTop: 0 }}>
                    Resumen de operación
                </Title>

                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    <Row label="CARGOs a liquidar" value={localCargos.length} isCount />
                    <Row
                        label="Total a liquidar"
                        value={formatPrice(batchTotal)}
                        valueColor="#cf1322"
                    />
                    <Row
                        label="Crédito disponible"
                        value={formatPrice(availableCredit)}
                        valueColor="#52c41a"
                    />
                    <Divider style={{ margin: '4px 0' }} />
                    {canSettle ? (
                        <Row
                            label="Crédito restante"
                            value={formatPrice(creditAfter)}
                            valueColor={creditAfter >= 0 ? '#52c41a' : '#cf1322'}
                            bold
                        />
                    ) : (
                        <Row
                            label="Crédito faltante"
                            value={`– ${formatPrice(shortfall)}`}
                            valueColor="#cf1322"
                            bold
                        />
                    )}
                </div>
            </div>

            {/* Insufficient credit alert */}
            {!canSettle && localCargos.length > 0 && (
                <Alert
                    type="warning"
                    showIcon
                    icon={<ExclamationCircleOutlined />}
                    style={{ marginTop: 12 }}
                    message={`Crédito insuficiente — Faltan ${formatPrice(shortfall)}`}
                    description="Podés quitar CARGOs de la lista o registrar un pago/adelanto adicional antes de liquidar."
                />
            )}

            {/* Disclaimer note */}
            <Alert
                type="info"
                showIcon
                icon={<InfoCircleOutlined />}
                style={{ marginTop: 12 }}
                message="Esta operación no genera un nuevo ingreso de caja."
                description={
                    'El dinero ya fue registrado cuando se cargó el pago o adelanto correspondiente. ' +
                    'La liquidación aplica ese crédito existente contra los CARGOs seleccionados.'
                }
            />
        </Modal>
    );
};

// Small helper component for summary rows
const Row = ({ label, value, valueColor, bold, isCount }) => (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Text type="secondary">{label}</Text>
        <Text
            strong={bold}
            style={{ color: valueColor, fontSize: isCount ? 14 : 15 }}
        >
            {isCount ? value : value}
        </Text>
    </div>
);

export default LiquidarCargoModal;
